import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { affiliatesApi, features, linksApi } from '@/lib/api';
import {
  CursorArrowRaysIcon,
  UserPlusIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  RocketLaunchIcon,
  ClipboardIcon,
  BuildingOfficeIcon,
  CheckBadgeIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface Stats {
  totalClicks: number;
  conversions: number;
  totalEarnings: number;
  pendingEarnings: number;
  conversionRate: number;
}

interface AffiliateLink {
  linkId: string;
  shortCode: string;
  destinationUrl: string;
  name?: string;
  clicks: number;
  conversions: number;
}

interface AffiliateProfile {
  affiliate: {
    affiliateId: string;
    referralCode: string;
    status: string;
    tier: string;
  };
  organization: {
    organizationId: string;
    name: string;
    slug: string;
    logo?: string;
  } | null;
  commission: {
    type: string;
    value: number;
    cookieDuration: number;
  } | null;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export default function AffiliateDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [profile, setProfile] = useState<AffiliateProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [primaryLink, setPrimaryLink] = useState<AffiliateLink | null>(null);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsResponse, profileResponse, linksResponse] = await Promise.all([
          affiliatesApi.getMyStats(),
          affiliatesApi.getMe(),
          linksApi.list().catch(() => ({ data: { success: false, data: { links: [] } } })),
        ]);

        if (statsResponse.data?.success) {
          setStats(statsResponse.data?.data?.stats ?? null);
        }

        if (profileResponse.data?.success) {
          setProfile(profileResponse.data?.data ?? null);
        }

        const links = linksResponse.data?.data?.links ?? [];
        if (linksResponse.data?.success && links.length > 0) {
          setPrimaryLink(links[0]);
        }
      } catch (error: unknown) {
        const err = error as { response?: { status?: number; data?: { error?: { code?: string } } } };
        // Check if user needs to join a program first
        if (err.response?.status === 404 && err.response?.data?.error?.code === 'NO_AFFILIATE_PROFILE') {
          setNeedsOnboarding(true);
        } else if (err.response?.status === 403) {
          // Profile not found - needs onboarding
          setNeedsOnboarding(true);
        } else {
          console.error('Failed to fetch stats:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Tracking URLs go through the API, not the frontend
  const apiUrl = import.meta.env.VITE_API_URL || '';

  const handleCopyLink = () => {
    if (primaryLink) {
      const trackingUrl = `${apiUrl}/t/${primaryLink.shortCode}`;
      navigator.clipboard.writeText(trackingUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  // Show onboarding message for new affiliates
  if (needsOnboarding) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-rust/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <RocketLaunchIcon className="w-10 h-10 text-rust" />
          </div>
          <h2 className="text-2xl font-display font-bold text-charcoal mb-3">Welcome to Venndela!</h2>
          <p className="text-charcoal/60 max-w-md mx-auto mb-8">
            You're all set up as an affiliate. Now it's time to find affiliate programs to join
            and start earning commissions.
          </p>
          <Link
            to="/catalog"
            className="btn btn-primary px-8 py-3 text-lg inline-flex items-center gap-2"
          >
            <MagnifyingGlassIcon className="w-5 h-5" />
            Browse Affiliate Programs
          </Link>
        </div>

        {/* Getting Started Steps */}
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/30">
            <h3 className="text-lg font-display font-semibold text-charcoal">Getting Started</h3>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-rust rounded-full flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div>
                  <h4 className="text-charcoal font-medium">
                    {features.marketplace ? 'Browse the Marketplace' : 'Find Affiliate Programs'}
                  </h4>
                  <p className="text-charcoal/60 text-sm mt-1">
                    {features.marketplace
                      ? 'Explore our catalog of affiliate programs from various organizations. Find programs that match your audience and interests.'
                      : 'Connect with organizations looking for affiliates. Find programs that match your audience and interests.'}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-tan rounded-full flex items-center justify-center text-charcoal font-bold">
                  2
                </div>
                <div>
                  <h4 className="text-charcoal font-medium">Apply to Programs</h4>
                  <p className="text-charcoal/60 text-sm mt-1">
                    Submit applications to programs you'd like to promote.
                    Some programs auto-approve, others review applications manually.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-sage rounded-full flex items-center justify-center text-white font-bold">
                  3
                </div>
                <div>
                  <h4 className="text-charcoal font-medium">Create Links & Earn</h4>
                  <p className="text-charcoal/60 text-sm mt-1">
                    Once approved, create tracking links and start promoting.
                    Earn commissions on every sale you generate!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      name: 'Total Clicks',
      value: formatNumber(stats?.totalClicks || 0),
      icon: CursorArrowRaysIcon,
      accentColor: 'bg-sage',
    },
    {
      name: 'Conversions',
      value: formatNumber(stats?.conversions || 0),
      icon: UserPlusIcon,
      accentColor: 'bg-rust',
    },
    {
      name: 'Total Earnings',
      value: formatCurrency(stats?.totalEarnings || 0),
      icon: CurrencyDollarIcon,
      accentColor: 'bg-tan',
    },
    {
      name: 'Pending Balance',
      value: formatCurrency(stats?.pendingEarnings || 0),
      icon: BanknotesIcon,
      accentColor: 'bg-charcoal',
    },
  ];

  // Determine affiliate status states
  const isPending = profile?.affiliate?.status === 'pending';
  const isApproved = profile?.affiliate?.status === 'approved';
  const isNewlyApproved = isApproved && profile?.organization && !primaryLink;

  return (
    <div className="space-y-6">
      {/* Pending Review Banner */}
      {isPending && profile?.organization && showWelcomeBanner && (
        <div className="rounded-xl bg-tan/20 border border-tan/40 p-6 relative">
          <button
            onClick={() => setShowWelcomeBanner(false)}
            className="absolute top-4 right-4 text-charcoal/40 hover:text-charcoal/60 transition-colors"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-tan/30 rounded-full flex items-center justify-center flex-shrink-0">
              <ClockIcon className="w-7 h-7 text-charcoal/70" />
            </div>
            <div className="pr-8">
              <h3 className="font-display text-lg font-bold text-charcoal mb-1">
                Your application is under review
              </h3>
              <p className="text-charcoal/70 text-sm">
                Thanks for applying to {profile.organization.name}'s affiliate program!
                Your application is being reviewed and you'll be notified once a decision is made.
                This typically takes 1-2 business days.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Banner for Auto-Approved Affiliates */}
      {isNewlyApproved && showWelcomeBanner && (
        <div className="rounded-xl bg-sage/10 border border-sage/30 p-6 relative">
          <button
            onClick={() => setShowWelcomeBanner(false)}
            className="absolute top-4 right-4 text-sage/60 hover:text-sage transition-colors"
            aria-label="Dismiss"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-sage/20 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircleIcon className="w-7 h-7 text-sage" />
            </div>
            <div className="pr-8">
              <h3 className="font-display text-lg font-bold text-charcoal mb-1">
                You're approved for {profile?.organization?.name}!
              </h3>
              <p className="text-charcoal/70 text-sm mb-4">
                Great news! You've been automatically approved as an affiliate.
                You can start creating tracking links right away and earn commissions on referrals.
              </p>
              <Link
                to="/links"
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <LinkIcon className="w-4 h-4" />
                Create Your First Link
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Active Program Card */}
      {profile?.organization && (
        <div className="rounded-xl bg-warm-white shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 ${isPending ? 'bg-tan/20' : 'bg-sage/10'} rounded-xl flex items-center justify-center`}>
                {profile.organization.logo ? (
                  <img
                    src={profile.organization.logo}
                    alt={profile.organization.name}
                    className="w-10 h-10 object-contain rounded"
                  />
                ) : (
                  <BuildingOfficeIcon className={`w-7 h-7 ${isPending ? 'text-charcoal/50' : 'text-sage'}`} />
                )}
              </div>
              <div>
                <p className="text-xs text-charcoal/50 uppercase tracking-wide mb-0.5">
                  {isPending ? 'Pending Program' : 'Active Program'}
                </p>
                <h3 className="font-display text-xl font-bold text-charcoal">{profile.organization.name}</h3>
                <div className="flex items-center gap-3 mt-1">
                  {isPending && (
                    <span className="inline-flex items-center gap-1 text-xs text-charcoal/60 font-medium">
                      <ClockIcon className="w-4 h-4" />
                      Pending Review
                    </span>
                  )}
                  {isApproved && (
                    <span className="inline-flex items-center gap-1 text-xs text-sage font-medium">
                      <CheckBadgeIcon className="w-4 h-4" />
                      Approved
                    </span>
                  )}
                  {profile.commission && isApproved && (
                    <span className="text-xs text-charcoal/60">
                      {profile.commission.value}% commission • {profile.commission.cookieDuration} day cookie
                    </span>
                  )}
                </div>
              </div>
            </div>
            {isApproved && (
              <Link
                to="/links"
                className="btn btn-primary"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Create Link
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Stats Grid with accent bars */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="rounded-xl bg-warm-white shadow-sm p-6 relative overflow-hidden"
          >
            {/* Left accent bar */}
            <div className={`absolute top-0 left-0 w-1 h-full ${stat.accentColor}`} />
            <div className="pl-2">
              <p className="text-sm text-charcoal/60 mb-1">{stat.name}</p>
              <p className="font-serif text-3xl text-charcoal leading-none mb-1">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Two column layout - Chart and Referral Link */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart Card */}
        <div className="lg:col-span-2 rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20 flex items-center justify-between">
            <h3 className="font-display text-lg font-bold text-charcoal">Performance</h3>
            <Link to="/commissions" className="text-sm text-rust font-medium hover:underline">
              View Details →
            </Link>
          </div>
          <div className="p-6">
            {/* Performance Summary */}
            <div className="h-48 rounded-lg flex flex-col items-center justify-center bg-cream/30">
              <ChartBarIcon className="w-12 h-12 text-charcoal/20 mb-3" />
              <p className="text-charcoal/50 text-sm mb-4">Performance analytics coming soon</p>
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <p className="font-serif text-2xl text-sage">{stats?.conversions || 0}</p>
                  <p className="text-xs text-charcoal/50">Conversions</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-sage">{formatCurrency(stats?.totalEarnings || 0)}</p>
                  <p className="text-xs text-charcoal/50">Total Earnings</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Link Card */}
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20">
            <h3 className="font-display text-lg font-bold text-charcoal">
              {profile?.organization ? `${profile.organization.name} Referral Link` : 'Your Referral Link'}
            </h3>
          </div>
          <div className="p-6">
            {isPending ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-tan/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ClockIcon className="w-6 h-6 text-charcoal/50" />
                </div>
                <p className="text-charcoal/60 text-sm">
                  You'll be able to create referral links once your application is approved.
                </p>
              </div>
            ) : primaryLink ? (
              <>
                {/* Referral Link Box */}
                <div className="bg-cream rounded-lg p-4 mb-4">
                  <p className="text-xs text-charcoal/60 mb-2">
                    Share this tracking link to earn commissions
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${apiUrl}/t/${primaryLink.shortCode}`}
                      className="flex-1 px-3 py-2 text-sm bg-warm-white border border-tan/30 rounded-md font-mono text-charcoal"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-2 bg-charcoal text-warm-white rounded-md font-medium text-sm hover:bg-rust transition-colors flex items-center gap-1"
                    >
                      <ClipboardIcon className="w-4 h-4" />
                      Copy
                    </button>
                  </div>
                  {primaryLink.destinationUrl && (
                    <p className="text-xs text-charcoal/50 mt-2">
                      → Redirects to: <span className="text-charcoal/70">{primaryLink.destinationUrl}</span>
                    </p>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-cream rounded-lg p-3 text-center">
                    <p className="font-serif text-2xl text-charcoal">
                      {(stats?.conversionRate || 0).toFixed(1)}%
                    </p>
                    <p className="text-xs text-charcoal/60">Conversion Rate</p>
                  </div>
                  <div className="bg-cream rounded-lg p-3 text-center">
                    <p className="font-serif text-2xl text-charcoal">
                      {formatNumber(primaryLink.clicks ?? 0)}
                    </p>
                    <p className="text-xs text-charcoal/60">Total Clicks</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-charcoal/60 mb-4">
                  {profile?.organization
                    ? `Create a referral link for ${profile.organization.name}`
                    : 'No referral links yet'}
                </p>
                <Link to="/links" className="btn btn-primary">
                  Create Your First Link
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {isApproved && (
        <div className="rounded-xl bg-warm-white shadow-sm">
          <div className="px-6 py-4 border-b border-tan/20">
            <h3 className="font-display text-lg font-bold text-charcoal">Quick Actions</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link
                to="/links"
                className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="w-10 h-10 bg-rust/10 rounded-lg flex items-center justify-center">
                  <LinkIcon className="w-5 h-5 text-rust" />
                </div>
                <div>
                  <p className="font-medium text-charcoal">Create Link</p>
                  <p className="text-sm text-charcoal/60">Generate affiliate links</p>
                </div>
              </Link>

              <Link
                to="/commissions"
                className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                  <CurrencyDollarIcon className="w-5 h-5 text-sage" />
                </div>
                <div>
                  <p className="font-medium text-charcoal">View Commissions</p>
                  <p className="text-sm text-charcoal/60">Track your earnings</p>
                </div>
              </Link>

              <Link
                to="/payouts"
                className="flex items-center gap-4 p-4 bg-cream rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                <div className="w-10 h-10 bg-charcoal/10 rounded-lg flex items-center justify-center">
                  <BanknotesIcon className="w-5 h-5 text-charcoal" />
                </div>
                <div>
                  <p className="font-medium text-charcoal">Request Payout</p>
                  <p className="text-sm text-charcoal/60">Withdraw your balance</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
